// cspell:ignore rosé winemaking ageability Vivino aperitif digestif terroir minerality Ginjo Junmai Daiginjo cidery mashbill collectibility reposado upsell upselling disruptors umami BJCP Sommel variabley robusta Rooibos Honjozo mocktail Mocktail Ribeye Simcoe Macallan biodynamic negroni campari Bourguignon Osso Buco Reggliano Ragu portobello Safale fudgy Carnitas sauvignon sonoma sommelai tesseract multitenant firestore
/**
 * SommelAI AI Service - Legacy Core Functions
 *
 * Core AI functions for beverage intelligence. This service provides the foundational
 * AI capabilities that are used by the newer Phase 1 and Phase 2 services.
 *
 * Features:
 * - Wine analysis and enhancement with terroir insights
 * - Spirit profiling and distillery information
 * - Beer style classification and brewing details
 * - Cocktail recipe optimization and pairing suggestions
 * - Food pairing recommendations across all beverages
 * - Personalized recommendations based on taste preferences
 * - Market insights and investment guidance
 * - Real-time inventory management and optimization
 *
 * Supports both Anthropic Claude and Google Gemini Pro APIs
 *
 * Note: For new implementations, consider using:
 * - aiServiceIntegration.js for multi-model AI orchestration
 * - dataEnhancementPipeline.js for comprehensive enhancement
 * - masterServiceOrchestrator.js for unified service access
 */

// Import AI Service Integration for delegation
import { AIServiceIntegration } from "./aiServiceIntegration.js";

// API Configuration
const ANTHROPIC_API_KEY = process.env.REACT_APP_ANTHROPIC_API_KEY;
const ANTHROPIC_API_URL = "https://api.anthropic.com/v1/messages";
const GEMINI_API_KEY = process.env.REACT_APP_GEMINI_API_KEY;
const GEMINI_API_URL =
  "https://generativelanguage.googleapis.com/v1beta/models/gemini-pro:generateContent";

// Enhanced AI Model Selection Based on Task
export const AI_MODELS = {
  WINE_ANALYSIS: "claude-3-5-sonnet-20241022",
  SPIRIT_PROFILING: "claude-3-5-sonnet-20241022",
  BEER_CLASSIFICATION: "gemini-pro",
  COCKTAIL_OPTIMIZATION: "gemini-pro",
  FOOD_PAIRING: "claude-3-5-sonnet-20241022",
  PERSONALIZATION: "gemini-pro",
  MARKET_ANALYSIS: "claude-3-5-sonnet-20241022",
  INVENTORY_OPTIMIZATION: "gemini-pro",
};

/**
 * Generate tasting notes for a wine using AI
 */
export const generateTastingNotes = async (wineInfo) => {
  const prompt = `Generate detailed tasting notes for ${wineInfo.name} from ${
    wineInfo.winery || "unknown winery"
  }, 
    ${wineInfo.region || ""} ${wineInfo.country || ""}. 
    ${wineInfo.varietal ? `It's a ${wineInfo.varietal}.` : ""}
    ${wineInfo.vintage ? `Vintage: ${wineInfo.vintage}.` : ""}
    
    Format your response as JSON with the following structure:
    {
      "tastingNotes": "Comprehensive tasting notes paragraph",
      "nose": "Aromas detected on the nose",
      "palate": "Flavors detected on the palate"
    }`;

  return callAnthropicAI(prompt);
};

/**
 * Generate ratings for a wine using AI
 */
export const generateRatings = async (wineInfo) => {
  const prompt = `Generate realistic and plausible ratings from wine publications for ${
    wineInfo.name
  } from ${wineInfo.winery || "unknown winery"}, 
    ${wineInfo.region || ""} ${wineInfo.country || ""}. 
    ${wineInfo.varietal ? `It's a ${wineInfo.varietal}.` : ""}
    ${wineInfo.vintage ? `Vintage: ${wineInfo.vintage}.` : ""}
    
    Format your response as JSON with the following structure:
    {
      "ratings": {
        "Wine Spectator": "a number between 80-100",
        "Wine Enthusiast": "a number between 80-100",
        "James Suckling": "a number between 80-100"
      }
    }`;

  return callAnthropicAI(prompt);
};

/**
 * Generate image URL suggestions for a wine using AI
 */
export const generateImageSuggestions = async (wineInfo) => {
  const prompt = `Suggest URLs for wine label or bottle images of ${
    wineInfo.name
  } from ${wineInfo.winery || "unknown winery"}.
    Format your response as JSON with the following structure:
    {
      "imageUrl": "URL to a royalty-free or public domain image of this wine label or bottle"
    }`;

  return callAnthropicAI(prompt);
};

/**
 * Generate comprehensive wine information including varietals, percentages, tasting notes, and professional reviews
 */
export const generateComprehensiveWineInfo = async (wineInfo) => {
  const wineName = wineInfo.name || "";
  const wineDescription = wineInfo.description || "";

  const prompt = `Generate comprehensive wine information for "${wineName}" ${wineDescription}. 
    
    Please provide:
    1. Grape varietals with percentages (should add up to 100%)
    2. Detailed tasting notes including nose, palate, and finish
    3. Professional ratings from wine publications
    4. Additional wine details like vintage, alcohol content, etc.
    
    Format your response as JSON with the following structure:
    {
      "varietals": [
        {
          "grape": "grape variety name",
          "percentage": number
        }
      ],
      "tastingNotes": {
        "appearance": "description of color and clarity",
        "nose": "aromas detected on the nose",
        "palate": "flavors detected on the palate", 
        "finish": "description of the finish"
      },
      "professionalReviews": {
        "Wine Spectator": {
          "rating": number between 80-100,
          "review": "brief review excerpt"
        },
        "Wine Enthusiast": {
          "rating": number between 80-100,
          "review": "brief review excerpt"
        },
        "James Suckling": {
          "rating": number between 80-100,
          "review": "brief review excerpt"
        }
      },
      "wineDetails": {
        "vintage": "year or NV for non-vintage",
        "alcoholContent": "percentage",
        "servingTemperature": "temperature range",
        "cellaring": "aging potential",
        "foodPairings": ["pairing 1", "pairing 2", "pairing 3"]
      }
    }`;

  return callAnthropicAI(prompt);
};

/**
 * Generate enhanced wine classification and location information
 */
export const generateWineClassificationInfo = async (wineInfo) => {
  const wineName = wineInfo.name || "";
  const wineDescription = wineInfo.description || "";
  const currentRegion = wineInfo.region || "";
  const currentCountry = wineInfo.country || "";

  const prompt = `Analyze this wine and provide enhanced classification and location information for "${wineName}" ${wineDescription}.
    Current region: ${currentRegion}, Current country: ${currentCountry}
    
    Please provide:
    1. Wine type classification (red, white, rosé, sparkling, dessert wine, fortified, etc.)
    2. Proper country and region information
    3. Specific appellations if applicable
    4. Vintage-specific information about growing conditions and winemaker notes
    5. Winery history and information
    
    Format your response as JSON with the following structure:
    {
      "wineType": "red/white/rosé/sparkling/dessert/fortified/etc",
      "location": {
        "country": "proper country name",
        "region": "proper region name", 
        "appellation": "specific appellation or AVA if applicable",
        "subRegion": "sub-region if applicable"
      },
      "vintageInfo": {
        "vintage": "year if determinable",
        "growingConditions": "description of that year's growing conditions in the region",
        "winemaker": "winemaker notes about this vintage",
        "harvestNotes": "information about the harvest of this vintage"
      },
      "wineryInfo": {
        "established": "year winery was established if known",
        "history": "brief history of the winery",
        "philosophy": "winemaking philosophy",
        "accolades": "notable awards or recognition",
        "signature": "what the winery is known for"
      }
    }`;

  return callAnthropicAI(prompt);
};

/**
 * Generate comprehensive wine comparison analysis using AI
 */
export const generateWineComparison = async (wines) => {
  if (!wines || wines.length < 2) {
    throw new Error("At least 2 wines are required for comparison");
  }

  const wineDescriptions = wines
    .map(
      (wine, index) =>
        `Wine ${index + 1}: ${wine.name} from ${
          wine.winery || "unknown winery"
        }, 
    ${wine.region || ""} ${wine.country || ""}. 
    ${wine.varietal ? `Varietal: ${wine.varietal}.` : ""}
    ${wine.vintage ? `Vintage: ${wine.vintage}.` : ""}
    ${wine.price ? `Price: $${wine.price}.` : ""}
    ${wine.description ? "Description: " + wine.description : ""}`
    )
    .join("\n\n");

  const prompt = `Provide a comprehensive comparison analysis of these ${wines.length} wines:

${wineDescriptions}

Please provide a detailed JSON response with the following structure:
{
  "overallComparison": "A comprehensive overview comparing all wines including their styles, origins, and characteristics",
  "wines": [
    {
      "name": "Wine name",
      "tastingProfile": {
        "body": "Light/Medium/Full",
        "acidity": "Low/Medium/High",
        "tannins": "Low/Medium/High",
        "sweetness": "Dry/Off-dry/Sweet",
        "alcohol": "Low/Medium/High",
        "complexity": "Simple/Moderate/Complex"
      },
      "tastingNotes": {
        "appearance": "Visual characteristics",
        "aroma": "Primary aromas and bouquet",
        "flavor": "Flavor profile and finish",
        "texture": "Mouthfeel and structure"
      },
      "foodPairings": [
        "Specific food pairing 1",
        "Specific food pairing 2",
        "Specific food pairing 3"
      ],
      "servingRecommendations": {
        "temperature": "Ideal serving temperature",
        "glassware": "Recommended glass type",
        "decanting": "Decanting recommendation"
      },
      "ageability": "Aging potential and drinking window",
      "valueAssessment": "Value for money assessment",
      "uniqueCharacteristics": "What makes this wine distinctive"
    }
  ],
  "headToHeadComparison": {
    "bodyComparison": "Compare the body/weight of all wines",
    "acidityComparison": "Compare acidity levels",
    "tanninComparison": "Compare tannin structures",
    "flavorIntensity": "Compare flavor intensity and complexity",
    "foodPairingVersatility": "Which wine pairs best with different food types",
    "priceValueComparison": "Best value for money comparison",
    "occasionRecommendations": "Which wine for which occasion"
  },
  "recommendations": {
    "bestForBeginners": "Which wine is most approachable",
    "bestWithFood": "Best food pairing wine",
    "bestValue": "Best value for money",
    "mostUnique": "Most distinctive or interesting wine",
    "cellarWorthy": "Best for aging/collecting"
  }
}

Please provide realistic, detailed, and informative comparisons based on the wine types, regions, and characteristics. Be specific about flavor notes, structural elements, and practical recommendations.`;

  return callAnthropicAI(prompt);
};

/**
 * Generate wine image URLs from multiple sources
 */
export const generateWineImages = async (wineInfo) => {
  const wineName = wineInfo.name || "";
  const winery = wineInfo.winery || wineInfo.producer || "";

  // Generate multiple potential image sources
  const imageUrls = {
    label: null,
    bottle: null,
    sources: [],
  };

  // Try to find images from wine databases
  const possibleSources = [
    {
      name: "Vivino",
      baseUrl: "https://images.vivino.com/",
      searchPattern: `${wineName} ${winery}`
        .toLowerCase()
        .replace(/[^a-z0-9\s]/g, "")
        .replace(/\s+/g, "-"),
    },
    {
      name: "Wine-Searcher",
      baseUrl: "https://www.wine-searcher.com/images/",
      searchPattern: `${wineName}-${winery}`
        .toLowerCase()
        .replace(/[^a-z0-9\s]/g, "")
        .replace(/\s+/g, "-"),
    },
    {
      name: "CellarTracker",
      baseUrl: "https://www.cellartracker.com/images/",
      searchPattern: `${winery}-${wineName}`
        .toLowerCase()
        .replace(/[^a-z0-9\s]/g, "")
        .replace(/\s+/g, "-"),
    },
  ];

  // For demonstration, we'll generate structured URLs that could work
  // In a real implementation, you'd want to use actual wine image APIs
  if (wineName && winery) {
    const cleanName = wineName.toLowerCase().replace(/[^a-z0-9]/g, "");
    const cleanWinery = winery.toLowerCase().replace(/[^a-z0-9]/g, "");

    // Generate potential Vivino-style URLs (most common wine image source)
    imageUrls.label = `https://images.vivino.com/thumbs/${cleanWinery}-${cleanName}-label_600x600.png`;
    imageUrls.bottle = `https://images.vivino.com/thumbs/${cleanWinery}-${cleanName}-bottle_600x600.png`;

    imageUrls.sources = possibleSources.map((source) => ({
      name: source.name,
      url: `${source.baseUrl}${source.searchPattern}_600x600.png`,
      type: "label",
    }));
  }

  return imageUrls;
};

/**
 * Enhanced wine image search using AI to suggest proper image URLs
 */
export const generateWineImageSuggestions = async (wineInfo) => {
  const prompt = `Find wine label and bottle images for this wine:
Name: ${wineInfo.name}
Winery/Producer: ${wineInfo.winery || wineInfo.producer || "Unknown"}
Region: ${wineInfo.region || ""}
Vintage: ${wineInfo.vintage || ""}
Description: ${wineInfo.description || ""}

Please provide image URLs from legitimate wine databases like Vivino, Wine-Searcher, or CellarTracker.
Format your response as JSON:
{
  "images": {
    "label": "URL to wine label image",
    "bottle": "URL to bottle image", 
    "alternative": "Alternative image URL",
    "source": "Source website name",
    "attribution": "Proper attribution text"
  },
  "searchTerms": ["term1", "term2", "term3"],
  "fallbackImages": {
    "generic": "URL to generic wine bottle/label image for this wine type",
    "placeholder": "URL to placeholder image"
  }
}

Prioritize:
1. Official winery images
2. Vivino database images
3. Wine-Searcher images
4. High-quality generic images for the wine type
5. Placeholder images as last resort

Ensure all URLs are valid and point to actual wine images.`;

  return callAnthropicAI(prompt);
};

/**
 * Call the Anthropic Claude API with a prompt and optional model specification
 */
export const callAnthropicAI = async (
  prompt,
  model = "claude-3-5-sonnet-20241022"
) => {
  try {
    const response = await fetch(ANTHROPIC_API_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-api-key": ANTHROPIC_API_KEY,
        "anthropic-version": "2023-06-01",
      },
      body: JSON.stringify({
        model: model,
        max_tokens: 1024,
        messages: [
          {
            role: "user",
            content: prompt,
          },
        ],
      }),
    });

    if (!response.ok) {
      throw new Error(`Anthropic API error: ${response.status}`);
    }

    const data = await response.json();

    // Parse the Claude response to extract the JSON
    try {
      const content = data.content[0].text;
      // Extract JSON if the response contains it
      const jsonMatch = content.match(/\{[\s\S]*\}/);
      return jsonMatch
        ? JSON.parse(jsonMatch[0])
        : { error: "Could not parse AI response" };
    } catch (error) {
      console.error("Error parsing AI response:", error);
      return { error: "Failed to parse AI response" };
    }
  } catch (error) {
    console.error("AI service error:", error);
    throw error;
  }
};

/**
 * Call the Google Gemini API with a prompt and optional model specification
 */
export const callGeminiAI = async (prompt, model = "gemini-pro") => {
  try {
    if (!GEMINI_API_KEY) {
      throw new Error("Gemini API key not configured");
    }

    const response = await fetch(`${GEMINI_API_URL}?key=${GEMINI_API_KEY}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        contents: [
          {
            parts: [
              {
                text: prompt,
              },
            ],
          },
        ],
      }),
    });

    if (!response.ok) {
      throw new Error(`Gemini API error: ${response.status}`);
    }

    const data = await response.json();
    const aiText = data.candidates?.[0]?.content?.parts?.[0]?.text;

    if (!aiText) {
      throw new Error("No AI response received");
    }

    // Parse the Gemini response to extract JSON
    try {
      const jsonMatch = aiText.match(/\{[\s\S]*\}/);
      return jsonMatch
        ? JSON.parse(jsonMatch[0])
        : { error: "Could not parse AI response", rawResponse: aiText };
    } catch (error) {
      console.error("Error parsing Gemini response:", error);
      return { error: "Failed to parse AI response", rawResponse: aiText };
    }
  } catch (error) {
    console.error("Gemini AI service error:", error);
    throw error;
  }
};

// === GEMINI PRO AI ENHANCEMENT FUNCTIONS ===

/**
 * Generate AI-enhanced drink information using Gemini Pro
 * @param {Object} drinkData - Basic drink information
 * @param {string} category - Category of drink (wine, spirit, cocktail, mocktail)
 * @returns {Promise<Object>} Enhanced drink information
 */
export async function enhanceDrinkWithAI(drinkData, category) {
  if (!GEMINI_API_KEY) {
    throw new Error("Gemini API key not configured");
  }

  try {
    const prompt = createPromptForCategory(drinkData, category);

    const response = await fetch(`${GEMINI_API_URL}?key=${GEMINI_API_KEY}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        contents: [
          {
            parts: [
              {
                text: prompt,
              },
            ],
          },
        ],
      }),
    });

    if (!response.ok) {
      throw new Error(`Gemini API error: ${response.status}`);
    }

    const data = await response.json();
    const aiText = data.candidates?.[0]?.content?.parts?.[0]?.text;

    if (!aiText) {
      throw new Error("No AI response received");
    }

    return parseAIResponse(aiText, category);
  } catch (error) {
    console.error("AI enhancement failed:", error);
    throw error;
  }
}

/**
 * Create category-specific prompts for AI enhancement
 */
function createPromptForCategory(drinkData, category) {
  const baseName = drinkData.name || "Unknown";
  const baseDescription = drinkData.description || "";

  switch (category.toLowerCase()) {
    case "wine":
      return `As a wine expert, provide detailed information about "${baseName}" wine. 
        Current description: "${baseDescription}"
        
        Please provide a JSON response with the following structure:
        {
          "description": "A detailed, engaging description suitable for a restaurant menu (2-3 sentences)",
          "varietals": ["list of grape varietals if applicable"],
          "region": "wine region/appellation",
          "vintage": "vintage year if applicable",
          "tastingNotes": "flavor profile and tasting notes",
          "pairings": ["food pairing suggestions"],
          "servingTemp": "ideal serving temperature",
          "alcoholContent": "alcohol percentage if known",
          "body": "light/medium/full body",
          "sweetness": "dry/off-dry/sweet",
          "category": "Red Wine/White Wine/Rosé/Sparkling/Dessert Wine"
        }`;

    case "spirit":
      return `As a spirits expert, provide detailed information about "${baseName}" spirit.
        Current description: "${baseDescription}"
        
        Please provide a JSON response with the following structure:
        {
          "description": "A detailed, engaging description suitable for a restaurant menu (2-3 sentences)",
          "type": "whiskey/vodka/gin/rum/tequila/cognac/etc",
          "origin": "country/region of origin",
          "age": "age statement if applicable",
          "alcoholContent": "alcohol percentage",
          "tastingNotes": "flavor profile and characteristics",
          "servingSuggestions": ["neat/on the rocks/in cocktails"],
          "distillery": "distillery or brand",
          "category": "Premium Spirit/Top Shelf/Call Brand"
        }`;

    case "cocktail":
      return `As a bartender/mixologist, provide detailed information about "${baseName}" cocktail.
        Current description: "${baseDescription}"
        
        Please provide a JSON response with the following structure:
        {
          "description": "A detailed, engaging description suitable for a restaurant menu (2-3 sentences)",
          "baseSpirit": "primary spirit used",
          "ingredients": ["list of main ingredients"],
          "garnish": "garnish description",
          "glassware": "type of glass served in",
          "preparationMethod": "shaken/stirred/built/muddled",
          "flavor": "flavor profile (sweet/sour/bitter/strong/refreshing)",
          "occasion": "best time to drink (aperitif/digestif/anytime)",
          "category": "Classic Cocktail/House Special/Signature/Seasonal"
        }`;

    case "beer":
      return `As a beer expert/cicerone, provide detailed information about "${baseName}" beer.
        Current description: "${baseDescription}"
        
        Please provide a JSON response with the following structure:
        {
          "description": "A detailed, engaging description suitable for a restaurant menu (2-3 sentences)",
          "beerStyle": "specific beer style (IPA, Stout, Lager, etc.)",
          "brewery": "brewery name",
          "origin": "country/region of origin",
          "alcoholContent": "alcohol percentage (ABV)",
          "ibu": "International Bitterness Units if applicable",
          "srm": "Standard Reference Method (color) if applicable",
          "tastingNotes": "flavor profile and tasting notes",
          "hopVarietals": ["hop varieties used"],
          "malt": "malt profile and characteristics",
          "pairings": ["food pairing suggestions"],
          "servingTemp": "ideal serving temperature",
          "beerCategory": "Ale/Lager/Stout/IPA/Wheat Beer/Sour/Pilsner",
          "body": "light/medium/full body",
          "category": "Craft Beer/Import/Domestic/Seasonal"
        }`;

    case "coffee":
      return `As a coffee expert/barista, provide detailed information about "${baseName}" coffee.
        Current description: "${baseDescription}"
        
        Please provide a JSON response with the following structure:
        {
          "description": "A detailed, engaging description suitable for a restaurant menu (2-3 sentences)",
          "coffeeName": "specific coffee name or blend",
          "coffeeType": "espresso/americano/latte/cappuccino/pour-over/cold brew/etc",
          "origin": "country/region where beans are from",
          "roastLevel": "Light/Medium/Medium-Dark/Dark",
          "brewMethod": "brewing method used",
          "tastingNotes": "flavor profile and tasting notes",
          "caffeine": "caffeine content level",
          "beanVariety": "arabica/robusta/blend specifics",
          "processingMethod": "washed/natural/honey processed",
          "servingStyle": ["hot/iced/specialty preparations"],
          "acidity": "Low/Medium/High",
          "body": "Light/Medium/Full",
          "category": "Espresso Drink/Specialty Coffee/Cold Brew/Single Origin"
        }`;

    case "tea":
      return `As a tea expert, provide detailed information about "${baseName}" tea.
        Current description: "${baseDescription}"
        
        Please provide a JSON response with the following structure:
        {
          "description": "A detailed, engaging description suitable for a restaurant menu (2-3 sentences)",
          "teaName": "specific tea name",
          "teaType": "Black/Green/White/Oolong/Pu-erh/Herbal/Rooibos",
          "origin": "country/region of origin",
          "steepTime": "optimal steeping time",
          "temperature": "water temperature for brewing",
          "tastingNotes": "flavor profile and characteristics",
          "caffeine": "caffeine content level",
          "healthBenefits": ["health benefits if applicable"],
          "season": "best season to enjoy",
          "ceremony": "traditional ceremony or preparation if applicable",
          "oxidation": "oxidation level for true teas",
          "category": "Traditional Tea/Herbal Blend/Premium/Ceremonial"
        }`;

    case "sake":
      return `As a sake expert, provide detailed information about "${baseName}" sake.
        Current description: "${baseDescription}"
        
        Please provide a JSON response with the following structure:
        {
          "description": "A detailed, engaging description suitable for a restaurant menu (2-3 sentences)",
          "sakeName": "specific sake name",
          "brewery": "brewery name (kura)",
          "region": "prefecture/region in Japan",
          "grade": "Junmai/Honjozo/Junmai Ginjo/Ginjo/Junmai Daiginjo/Daiginjo",
          "riceVariety": "rice variety used",
          "polishingRatio": "rice polishing ratio percentage",
          "alcoholContent": "alcohol percentage",
          "sakeValue": "SMV (Sake Meter Value) if known",
          "acidity": "acidity level",
          "tastingNotes": "flavor profile and characteristics",
          "servingTemp": ["serving temperature options"],
          "pairings": ["food pairing suggestions"],
          "style": "Dry/Sweet/Rich/Light/Aged",
          "category": "Premium Sake/Junmai/Ginjo/Daiginjo/Specialty"
        }`;

    case "cider":
      return `As a cider expert, provide detailed information about "${baseName}" cider.
        Current description: "${baseDescription}"
        
        Please provide a JSON response with the following structure:
        {
          "description": "A detailed, engaging description suitable for a restaurant menu (2-3 sentences)",
          "ciderName": "specific cider name",
          "cidery": "cidery name",
          "origin": "country/region of origin",
          "appleVarieties": ["apple varieties used"],
          "alcoholContent": "alcohol percentage",
          "sweetness": "Bone Dry/Dry/Off-Dry/Semi-Sweet/Sweet",
          "carbonation": "Still/Lightly Sparkling/Sparkling",
          "tastingNotes": "flavor profile and characteristics",
          "fermentation": "fermentation style and process",
          "aging": "aging process if applicable",
          "pairings": ["food pairing suggestions"],
          "servingTemp": "ideal serving temperature",
          "style": "Traditional/Modern/Heritage/Wild/Farmhouse",
          "category": "Craft Cider/Traditional/Modern/Specialty"
        }`;

    case "soft_drink":
      return `As a beverage expert, provide detailed information about "${baseName}" soft drink.
        Current description: "${baseDescription}"
        
        Please provide a JSON response with the following structure:
        {
          "description": "A detailed, engaging description suitable for a restaurant menu (2-3 sentences)",
          "drinkName": "specific drink name",
          "brand": "brand name",
          "category": "Cola/Lemon-Lime/Orange/Root Beer/Ginger Ale/Fruit Punch/Energy/Sports/Sparkling Water/Flavored Water",
          "flavor": "flavor profile and taste characteristics",
          "caffeine": "caffeine content if applicable",
          "calories": "calorie content per serving",
          "ingredients": ["key ingredients"],
          "carbonation": "Still/Lightly Carbonated/Carbonated/Highly Carbonated",
          "sweetener": "type of sweetener used",
          "servingStyle": ["bottle/can/fountain/glass"],
          "dietOptions": ["diet/zero/light versions if available"],
          "category": "Premium Soda/Classic/Diet/Natural/Artisanal"
        }`;

    case "mocktail":
      return `As a beverage expert, provide detailed information about "${baseName}" non-alcoholic cocktail/mocktail.
        Current description: "${baseDescription}"
        
        Please provide a JSON response with the following structure:
        {
          "description": "A detailed, engaging description suitable for a restaurant menu (2-3 sentences)",
          "baseIngredients": ["primary ingredients"],
          "allIngredients": ["complete ingredient list"],
          "garnish": "garnish description",
          "glassware": "type of glass served in",
          "flavor": "flavor profile (sweet/tart/refreshing/fruity)",
          "healthBenefits": "any health benefits if applicable",
          "occasion": "best time to drink",
          "category": "Refreshing Mocktail/Fruity Blend/Herbal Infusion"
        }`;

    default:
      return `Provide detailed information about "${baseName}" beverage.
        Current description: "${baseDescription}"
        
        Please provide a JSON response with enhanced description and relevant details.`;
  }
}

/**
 * Parse AI response and structure it properly
 */
function parseAIResponse(aiText, category) {
  try {
    // Extract JSON from the response (in case there's extra text)
    const jsonMatch = aiText.match(/\{[\s\S]*\}/);
    if (!jsonMatch) {
      throw new Error("No JSON found in AI response");
    }

    const parsedData = JSON.parse(jsonMatch[0]);

    // Add metadata
    parsedData.aiEnhanced = true;
    parsedData.aiTimestamp = new Date().toISOString();
    parsedData.aiSource = "Gemini Pro";
    parsedData.category = category;

    return parsedData;
  } catch (error) {
    console.error("Failed to parse AI response:", error);
    // Return a fallback enhanced description
    return {
      description: aiText.substring(0, 200) + "...",
      aiEnhanced: true,
      aiTimestamp: new Date().toISOString(),
      aiSource: "Gemini Pro",
      category: category,
      error: "Partial parsing - check original response",
    };
  }
}

/**
 * Get category-specific fields for form rendering
 */
export function getCategoryFields(category) {
  switch (category.toLowerCase()) {
    case "wine":
      return [
        { key: "varietals", label: "Grape Varietals", type: "array" },
        { key: "region", label: "Wine Region", type: "text" },
        { key: "vintage", label: "Vintage Year", type: "text" },
        { key: "tastingNotes", label: "Tasting Notes", type: "textarea" },
        { key: "pairings", label: "Food Pairings", type: "array" },
        { key: "servingTemp", label: "Serving Temperature", type: "text" },
        { key: "alcoholContent", label: "Alcohol %", type: "text" },
        {
          key: "body",
          label: "Body",
          type: "select",
          options: ["Light", "Medium", "Full"],
        },
        {
          key: "sweetness",
          label: "Sweetness",
          type: "select",
          options: ["Dry", "Off-Dry", "Sweet"],
        },
      ];

    case "spirit":
      return [
        { key: "type", label: "Spirit Type", type: "text" },
        { key: "origin", label: "Origin", type: "text" },
        { key: "age", label: "Age Statement", type: "text" },
        { key: "alcoholContent", label: "Alcohol %", type: "text" },
        { key: "tastingNotes", label: "Tasting Notes", type: "textarea" },
        {
          key: "servingSuggestions",
          label: "Serving Suggestions",
          type: "array",
        },
        { key: "distillery", label: "Distillery/Brand", type: "text" },
      ];

    case "cocktail":
      return [
        { key: "baseSpirit", label: "Base Spirit", type: "text" },
        { key: "ingredients", label: "Ingredients", type: "array" },
        { key: "garnish", label: "Garnish", type: "text" },
        { key: "glassware", label: "Glassware", type: "text" },
        {
          key: "preparationMethod",
          label: "Preparation",
          type: "select",
          options: ["Shaken", "Stirred", "Built", "Muddled", "Blended"],
        },
        { key: "flavor", label: "Flavor Profile", type: "text" },
        { key: "occasion", label: "Best Occasion", type: "text" },
      ];

    case "beer":
      return [
        { key: "beerStyle", label: "Beer Style", type: "text" },
        { key: "brewery", label: "Brewery", type: "text" },
        { key: "origin", label: "Origin", type: "text" },
        { key: "alcoholContent", label: "ABV %", type: "text" },
        { key: "ibu", label: "IBU (Bitterness)", type: "text" },
        { key: "srm", label: "SRM (Color)", type: "text" },
        { key: "tastingNotes", label: "Tasting Notes", type: "textarea" },
        { key: "hopVarietals", label: "Hop Varietals", type: "array" },
        { key: "malt", label: "Malt Profile", type: "text" },
        { key: "pairings", label: "Food Pairings", type: "array" },
        { key: "servingTemp", label: "Serving Temperature", type: "text" },
        {
          key: "beerCategory",
          label: "Category",
          type: "select",
          options: [
            "Ale",
            "Lager",
            "Stout",
            "IPA",
            "Wheat Beer",
            "Sour",
            "Pilsner",
          ],
        },
        {
          key: "body",
          label: "Body",
          type: "select",
          options: ["Light", "Medium", "Full"],
        },
      ];

    case "coffee":
      return [
        { key: "coffeeName", label: "Coffee Name", type: "text" },
        { key: "coffeeType", label: "Coffee Type", type: "text" },
        { key: "origin", label: "Origin/Region", type: "text" },
        {
          key: "roastLevel",
          label: "Roast Level",
          type: "select",
          options: ["Light", "Medium", "Medium-Dark", "Dark"],
        },
        { key: "brewMethod", label: "Brew Method", type: "text" },
        { key: "tastingNotes", label: "Tasting Notes", type: "textarea" },
        { key: "caffeine", label: "Caffeine Content", type: "text" },
        { key: "beanVariety", label: "Bean Variety", type: "text" },
        { key: "processingMethod", label: "Processing Method", type: "text" },
        { key: "servingStyle", label: "Serving Style", type: "array" },
        {
          key: "acidity",
          label: "Acidity",
          type: "select",
          options: ["Low", "Medium", "High"],
        },
        {
          key: "body",
          label: "Body",
          type: "select",
          options: ["Light", "Medium", "Full"],
        },
      ];

    case "tea":
      return [
        { key: "teaName", label: "Tea Name", type: "text" },
        {
          key: "teaType",
          label: "Tea Type",
          type: "select",
          options: [
            "Black",
            "Green",
            "White",
            "Oolong",
            "Pu-erh",
            "Herbal",
            "Rooibos",
          ],
        },
        { key: "origin", label: "Origin/Region", type: "text" },
        { key: "steepTime", label: "Steep Time", type: "text" },
        { key: "temperature", label: "Water Temperature", type: "text" },
        { key: "tastingNotes", label: "Tasting Notes", type: "textarea" },
        { key: "caffeine", label: "Caffeine Content", type: "text" },
        { key: "healthBenefits", label: "Health Benefits", type: "array" },
        { key: "season", label: "Best Season", type: "text" },
        { key: "ceremony", label: "Traditional Ceremony", type: "text" },
        { key: "oxidation", label: "Oxidation Level", type: "text" },
      ];

    case "sake":
      return [
        { key: "sakeName", label: "Sake Name", type: "text" },
        { key: "brewery", label: "Brewery (Kura)", type: "text" },
        { key: "region", label: "Prefecture/Region", type: "text" },
        {
          key: "grade",
          label: "Grade",
          type: "select",
          options: [
            "Junmai",
            "Honjozo",
            "Junmai Ginjo",
            "Ginjo",
            "Junmai Daiginjo",
            "Daiginjo",
          ],
        },
        { key: "riceVariety", label: "Rice Variety", type: "text" },
        { key: "polishingRatio", label: "Polishing Ratio", type: "text" },
        { key: "alcoholContent", label: "Alcohol %", type: "text" },
        { key: "sakeValue", label: "SMV (Sake Meter Value)", type: "text" },
        { key: "acidity", label: "Acidity", type: "text" },
        { key: "tastingNotes", label: "Tasting Notes", type: "textarea" },
        { key: "servingTemp", label: "Serving Temperature", type: "array" },
        { key: "pairings", label: "Food Pairings", type: "array" },
        {
          key: "style",
          label: "Style",
          type: "select",
          options: ["Dry", "Sweet", "Rich", "Light", "Aged"],
        },
      ];

    case "cider":
      return [
        { key: "ciderName", label: "Cider Name", type: "text" },
        { key: "cidery", label: "Cidery", type: "text" },
        { key: "origin", label: "Origin", type: "text" },
        { key: "appleVarieties", label: "Apple Varieties", type: "array" },
        { key: "alcoholContent", label: "ABV %", type: "text" },
        {
          key: "sweetness",
          label: "Sweetness",
          type: "select",
          options: ["Bone Dry", "Dry", "Off-Dry", "Semi-Sweet", "Sweet"],
        },
        {
          key: "carbonation",
          label: "Carbonation",
          type: "select",
          options: ["Still", "Lightly Sparkling", "Sparkling"],
        },
        { key: "tastingNotes", label: "Tasting Notes", type: "textarea" },
        { key: "fermentation", label: "Fermentation Style", type: "text" },
        { key: "aging", label: "Aging Process", type: "text" },
        { key: "pairings", label: "Food Pairings", type: "array" },
        { key: "servingTemp", label: "Serving Temperature", type: "text" },
        {
          key: "style",
          label: "Style",
          type: "select",
          options: ["Traditional", "Modern", "Heritage", "Wild", "Farmhouse"],
        },
      ];

    case "soft_drink":
      return [
        { key: "drinkName", label: "Drink Name", type: "text" },
        { key: "brand", label: "Brand", type: "text" },
        {
          key: "category",
          label: "Category",
          type: "select",
          options: [
            "Cola",
            "Lemon-Lime",
            "Orange",
            "Root Beer",
            "Ginger Ale",
            "Fruit Punch",
            "Energy",
            "Sports",
            "Sparkling Water",
            "Flavored Water",
          ],
        },
        { key: "flavor", label: "Flavor Profile", type: "text" },
        { key: "caffeine", label: "Caffeine Content", type: "text" },
        { key: "calories", label: "Calories", type: "text" },
        { key: "ingredients", label: "Key Ingredients", type: "array" },
        {
          key: "carbonation",
          label: "Carbonation",
          type: "select",
          options: [
            "Still",
            "Lightly Carbonated",
            "Carbonated",
            "Highly Carbonated",
          ],
        },
        { key: "sweetener", label: "Sweetener Type", type: "text" },
        { key: "servingStyle", label: "Serving Style", type: "array" },
        { key: "dietOptions", label: "Diet Options", type: "array" },
      ];

    case "mocktail":
      return [
        { key: "baseIngredients", label: "Base Ingredients", type: "array" },
        { key: "allIngredients", label: "All Ingredients", type: "array" },
        { key: "garnish", label: "Garnish", type: "text" },
        { key: "glassware", label: "Glassware", type: "text" },
        { key: "flavor", label: "Flavor Profile", type: "text" },
        { key: "healthBenefits", label: "Health Benefits", type: "text" },
        { key: "occasion", label: "Best Occasion", type: "text" },
      ];

    default:
      return [{ key: "description", label: "Description", type: "textarea" }];
  }
}

/**
 * Test Gemini API connection
 */
export async function testGeminiConnection() {
  if (!GEMINI_API_KEY) {
    return { success: false, error: "API key not configured" };
  }

  try {
    const response = await fetch(`${GEMINI_API_URL}?key=${GEMINI_API_KEY}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        contents: [
          {
            parts: [
              {
                text: 'Hello, this is a test. Please respond with "Connection successful".',
              },
            ],
          },
        ],
      }),
    });

    if (!response.ok) {
      return { success: false, error: `API error: ${response.status}` };
    }

    const data = await response.json();
    return {
      success: true,
      message: "Gemini API connection successful",
      response: data.candidates?.[0]?.content?.parts?.[0]?.text,
    };
  } catch (error) {
    return { success: false, error: error.message };
  }
}

// ===== BEER INTELLIGENCE FUNCTIONS =====

/**
 * Generate comprehensive beer information including style classification, brewing details, and tasting notes
 */
export const generateComprehensiveBeerInfo = async (beerInfo) => {
  const beerName = beerInfo.name || "";
  const beerDescription = beerInfo.description || "";

  const prompt = `Analyze and enhance information for this beer: "${beerName}" ${beerDescription}
    
    Please provide comprehensive beer analysis including:
    1. Beer style classification (IPA, Stout, Lager, etc.)
    2. Detailed brewing information
    3. Professional tasting notes
    4. Food pairing recommendations
    5. Serving suggestions
    
    Format your response as JSON with the following structure:
    {
      "beerStyle": {
        "category": "main category (Ale, Lager, Stout, etc.)",
        "subStyle": "specific style (American IPA, Russian Imperial Stout, etc.)",
        "bjcpCode": "BJCP style code if applicable",
        "originCountry": "country of origin for this style"
      },
      "brewingDetails": {
        "abv": "alcohol by volume percentage",
        "ibu": "International Bitterness Units",
        "srm": "Standard Reference Method (color)",
        "og": "Original Gravity",
        "fg": "Final Gravity",
        "hops": ["hop varieties used"],
        "malts": ["malt types used"],
        "yeast": "yeast strain type",
        "brewingProcess": "brief description of brewing process"
      },
      "tastingNotes": {
        "appearance": "color, clarity, head retention",
        "aroma": "hop, malt, and other aromas",
        "flavor": "taste profile and balance",
        "mouthfeel": "body, carbonation, finish",
        "overall": "overall impression"
      },
      "foodPairings": [
        "specific food pairing 1",
        "specific food pairing 2", 
        "specific food pairing 3",
        "specific food pairing 4"
      ],
      "servingDetails": {
        "temperature": "ideal serving temperature",
        "glassware": "recommended glass type",
        "garnish": "garnish recommendations if any"
      },
      "breweryInfo": {
        "establishedYear": "brewery founding year if known",
        "location": "brewery location",
        "brewingPhilosophy": "brewery's approach to brewing",
        "specialties": "what the brewery is known for"
      }
    }`;

  return callAnthropicAI(prompt, AI_MODELS.BEER_CLASSIFICATION);
};

/**
 * Generate beer and food pairing recommendations with detailed explanations
 */
export const generateBeerFoodPairings = async (
  beerInfo,
  foodContext = null
) => {
  const prompt = `Generate comprehensive food pairing recommendations for this beer:
    Name: ${beerInfo.name}
    Style: ${beerInfo.beerStyle?.subStyle || beerInfo.description}
    ABV: ${beerInfo.brewingDetails?.abv || "unknown"}
    IBU: ${beerInfo.brewingDetails?.ibu || "unknown"}
    ${foodContext ? `Food context: ${foodContext}` : ""}
    
    Format your response as JSON:
    {
      "primaryPairings": [
        {
          "food": "specific dish name",
          "reason": "why this pairing works",
          "preparation": "recommended preparation method",
          "season": "best season for this pairing"
        }
      ],
      "cuisineStyles": [
        {
          "cuisine": "cuisine type",
          "dishes": ["recommended dishes"],
          "explanation": "why this cuisine works"
        }
      ],
      "avoidPairings": [
        {
          "food": "food to avoid",
          "reason": "why it doesn't work"
        }
      ],
      "occasionPairings": {
        "bbq": "BBQ pairing suggestions",
        "gameDay": "Sports viewing pairings",
        "dinner": "Dinner course pairings",
        "dessert": "Dessert pairings if applicable"
      }
    }`;

  return callAnthropicAI(prompt, AI_MODELS.FOOD_PAIRING);
};

/**
 * Analyze beer trends and market positioning
 */
export const analyzeBeerMarketPosition = async (beerInfo) => {
  const prompt = `Analyze the market position and trends for this beer:
    Name: ${beerInfo.name}
    Style: ${beerInfo.beerStyle?.subStyle || beerInfo.description}
    Brewery: ${beerInfo.brewery || "unknown"}
    Price: ${beerInfo.price || "unknown"}
    
    Provide market analysis as JSON:
    {
      "marketPosition": "premium/craft/mainstream/value",
      "styleTrends": "current trends for this beer style",
      "competitiveAnalysis": "how it compares to similar beers",
      "targetAudience": "primary consumer demographic",
      "seasonality": "seasonal demand patterns",
      "growthPotential": "market growth assessment",
      "pricingStrategy": "pricing recommendations",
      "marketingOpportunities": ["marketing angle 1", "marketing angle 2"]
    }`;

  return callAnthropicAI(prompt, AI_MODELS.MARKET_ANALYSIS);
};

// ===== SPIRIT INTELLIGENCE FUNCTIONS =====

/**
 * Generate comprehensive spirit information including distillery heritage, production methods, and tasting profile
 */
export const generateComprehensiveSpiritInfo = async (spiritInfo) => {
  const spiritName = spiritInfo.name || "";
  const spiritDescription = spiritInfo.description || "";

  const prompt = `Analyze and enhance information for this spirit: "${spiritName}" ${spiritDescription}
    
    Please provide comprehensive spirit analysis including:
    1. Spirit classification and type
    2. Distillery information and heritage
    3. Production methods and ingredients
    4. Professional tasting notes
    5. Serving recommendations
    6. Investment and collectibility insights
    
    Format your response as JSON with the following structure:
    {
      "spiritClassification": {
        "category": "whiskey/vodka/gin/rum/tequila/cognac/brandy/liqueur/etc.",
        "subCategory": "specific type (single malt, bourbon, reposado, etc.)",
        "region": "geographic designation or appellation",
        "ageStatement": "age in years if applicable",
        "proof": "alcohol proof (ABV x 2)"
      },
      "distilleryInfo": {
        "name": "distillery name",
        "established": "founding year",
        "location": "full location details",
        "masterDistiller": "current master distiller if known",
        "heritage": "distillery history and tradition",
        "philosophy": "distilling philosophy and approach",
        "annualProduction": "production volume if known",
        "ownership": "current ownership structure"
      },
      "productionDetails": {
        "ingredients": {
          "grains": ["grain types used"],
          "botanicals": ["botanicals for gin/liqueurs"],
          "additives": ["any additives or flavorings"]
        },
        "mashbill": "grain recipe percentage if applicable",
        "distillationProcess": {
          "still": "type of still used",
          "distillations": "number of distillations",
          "cuts": "head/heart/tail cutting approach"
        },
        "maturation": {
          "barrels": "barrel type and treatment",
          "warehouse": "aging environment details",
          "climate": "impact of local climate",
          "angels_share": "evaporation rate if known"
        },
        "finishing": "any special finishing processes"
      },
      "tastingProfile": {
        "appearance": "color and clarity details",
        "nose": {
          "initial": "first impression aromas",
          "development": "evolving aromas with air",
          "intensity": "aroma strength (light/medium/intense)"
        },
        "palate": {
          "attack": "initial taste impression",
          "development": "flavor evolution on palate",
          "balance": "sweetness/spice/fruit balance",
          "complexity": "layering and depth"
        },
        "finish": {
          "length": "finish duration (short/medium/long)",
          "character": "finishing flavors",
          "warmth": "alcohol warmth level"
        },
        "overall": "overall tasting impression and quality"
      },
      "servingRecommendations": {
        "neat": {
          "glassware": "recommended glass",
          "temperature": "serving temperature",
          "water": "water addition recommendations"
        },
        "cocktails": ["suitable cocktail applications"],
        "pairings": {
          "cigars": "cigar pairing recommendations",
          "food": "food pairing suggestions",
          "cheese": "cheese pairing options"
        }
      },
      "collectibilityInsights": {
        "investmentGrade": "collectibility rating",
        "marketTrends": "current market performance", 
        "ageingPotential": "how it develops over time",
        "rarityFactors": "what makes it collectible",
        "priceProjection": "value trajectory expectations"
      }
    }`;

  return callAnthropicAI(prompt, AI_MODELS.SPIRIT_PROFILING);
};

/**
 * Generate spirit cocktail recommendations and recipes
 */
export const generateSpiritCocktailRecommendations = async (spiritInfo) => {
  const prompt = `Generate cocktail recommendations specifically for this spirit:
    Name: ${spiritInfo.name}
    Type: ${spiritInfo.spiritClassification?.category || spiritInfo.description}
    Profile: ${spiritInfo.tastingProfile?.overall || "unknown"}
    
    Provide cocktail recommendations as JSON:
    {
      "classicCocktails": [
        {
          "name": "cocktail name",
          "recipe": {
            "baseSpirit": "amount and type",
            "ingredients": ["ingredient with amount"],
            "garnish": "garnish description",
            "method": "preparation method",
            "glass": "glassware type"
          },
          "story": "brief history or story behind the cocktail",
          "whyItWorks": "why this spirit works in this cocktail"
        }
      ],
      "modernCreations": [
        {
          "name": "modern cocktail name",
          "recipe": {
            "baseSpirit": "amount and type",
            "ingredients": ["ingredient with amount"],
            "garnish": "garnish description",
            "method": "preparation method",
            "glass": "glassware type"
          },
          "concept": "cocktail concept and inspiration",
          "flavorProfile": "expected flavor outcome"
        }
      ],
      "seasonalVariations": {
        "spring": "spring cocktail suggestion",
        "summer": "summer cocktail suggestion", 
        "fall": "fall cocktail suggestion",
        "winter": "winter cocktail suggestion"
      },
      "mixingNotes": {
        "compatibility": "how well it mixes",
        "complementaryFlavors": ["flavors that enhance this spirit"],
        "avoidFlavors": ["flavors that clash"],
        "temperature": "mixing temperature considerations"
      }
    }`;

  return callAnthropicAI(prompt, AI_MODELS.COCKTAIL_OPTIMIZATION);
};

/**
 * Analyze spirit investment potential and market dynamics
 */
export const analyzeSpiritInvestmentPotential = async (spiritInfo) => {
  const prompt = `Analyze the investment potential for this spirit:
    Name: ${spiritInfo.name}
    Distillery: ${spiritInfo.distilleryInfo?.name || "unknown"}
    Age: ${spiritInfo.spiritClassification?.ageStatement || "unknown"}
    Production: ${spiritInfo.distilleryInfo?.annualProduction || "unknown"}
    
    Provide investment analysis as JSON:
    {
      "investmentRating": "A/B/C/D rating with explanation",
      "marketFactors": {
        "scarcity": "availability and production limits",
        "demandTrends": "consumer demand patterns",
        "brandStrength": "brand recognition and reputation",
        "ageStatement": "impact of age on value",
        "caskStrength": "impact of proof on collectibility"
      },
      "priceAnalysis": {
        "currentValue": "estimated current market value",
        "historicalTrends": "price movement over time",
        "futureProjection": "5-10 year value projection",
        "auctionPerformance": "auction market behavior"
      },
      "collectibilityFactors": {
        "limitedEditions": "special release status",
        "discontinuedExpression": "whether still in production",
        "awards": "major awards and recognition",
        "criticalScores": "professional ratings impact"
      },
      "riskAssessment": {
        "marketRisks": "potential market downturns",
        "liquidityRisks": "ease of resale",
        "storageRisks": "storage and condition requirements",
        "authenticity": "counterfeit risks and verification"
      },
      "recommendations": {
        "buyHold": "buy and hold strategy advice",
        "timing": "optimal buying/selling timing",
        "storage": "storage recommendations",
        "insurance": "insurance considerations"
      }
    }`;

  return callAnthropicAI(prompt, AI_MODELS.MARKET_ANALYSIS);
};

// ===== PERSONALIZATION & RECOMMENDATION ENGINE =====

/**
 * Generate personalized beverage recommendations based on user preferences and history
 */
export const generatePersonalizedRecommendations = async (
  userProfile,
  context = {}
) => {
  const prompt = `Generate personalized beverage recommendations for this user profile:
    
    User Preferences:
    - Favorite beverages: ${
      userProfile.favoriteBeverages?.join(", ") || "none specified"
    }
    - Disliked beverages: ${
      userProfile.dislikedBeverages?.join(", ") || "none specified"
    }
    - Preferred flavors: ${
      userProfile.preferredFlavors?.join(", ") || "none specified"
    }
    - Budget range: ${userProfile.budgetRange || "not specified"}
    - Experience level: ${userProfile.experienceLevel || "intermediate"}
    - Dietary restrictions: ${
      userProfile.dietaryRestrictions?.join(", ") || "none"
    }
    
    Context:
    - Occasion: ${context.occasion || "general"}
    - Season: ${context.season || "current"}
    - Food pairing: ${context.food || "none"}
    - Group size: ${context.groupSize || "1"}
    - Time of day: ${context.timeOfDay || "evening"}
    
    Provide personalized recommendations as JSON:
    {
      "recommendations": [
        {
          "category": "wine/spirit/beer/cocktail",
          "name": "specific beverage recommendation",
          "reason": "why this matches user preferences",
          "confidence": "high/medium/low confidence in this recommendation",
          "priceRange": "estimated price range",
          "availabilityNotes": "where to find it",
          "alternativeOptions": ["similar alternatives"]
        }
      ],
      "explorationSuggestions": [
        {
          "category": "beverage category to explore",
          "starter": "beginner-friendly option in this category",
          "progression": "next steps for developing taste",
          "reasoning": "why this would expand their palate"
        }
      ],
      "contextualPairings": {
        "food": "food pairing recommendations for context",
        "occasion": "how to present/serve for the occasion",
        "timing": "optimal timing for consumption"
      },
      "educationalOpportunities": [
        {
          "topic": "learning topic",
          "explanation": "brief educational content",
          "tastingExercise": "suggested tasting exercise"
        }
      ],
      "budgetOptimizations": {
        "bestValue": "best value recommendations within budget",
        "premiumWorthwhile": "premium options worth the splurge",
        "savingsTips": "how to get better value"
      }
    }`;

  return callGeminiAI(prompt, AI_MODELS.PERSONALIZATION);
};

/**
 * Analyze user taste preferences and generate flavor profile
 */
export const analyzeUserTasteProfile = async (beverageHistory, ratings) => {
  const prompt = `Analyze this user's beverage consumption history and ratings to create a taste profile:
    
    Beverage History:
    ${beverageHistory
      .map(
        (item) =>
          `- ${item.name} (${item.category}): rated ${item.rating}/5, notes: ${
            item.notes || "none"
          }`
      )
      .join("\n")}
    
    Create comprehensive taste profile as JSON:
    {
      "flavorPreferences": {
        "sweetness": "preference level (low/medium/high)",
        "acidity": "preference level (low/medium/high)", 
        "bitterness": "preference level (low/medium/high)",
        "umami": "preference level (low/medium/high)",
        "spiciness": "tolerance level (low/medium/high)",
        "alcohol": "tolerance level (low/medium/high)"
      },
      "aromaPreferences": [
        {
          "aroma": "specific aroma",
          "preference": "like/neutral/dislike",
          "confidence": "how certain we are"
        }
      ],
      "texturePreferences": {
        "body": "preferred body (light/medium/full)",
        "carbonation": "preferred carbonation level",
        "tannins": "tannin tolerance",
        "creaminess": "preference for creamy textures"
      },
      "categoryInsights": {
        "wines": {
          "redPreference": "preferred red wine styles",
          "whitePreference": "preferred white wine styles",
          "sparklingPreference": "sparkling wine preferences"
        },
        "spirits": {
          "preferredTypes": ["preferred spirit categories"],
          "proofTolerance": "preferred alcohol strength",
          "agePreference": "young vs aged spirits"
        },
        "beers": {
          "stylePreferences": ["preferred beer styles"],
          "hopTolerance": "hop tolerance level",
          "maltPreference": "malt character preferences"
        }
      },
      "adventurousness": {
        "score": "1-10 scale",
        "description": "willingness to try new things",
        "recommendations": "how to encourage exploration"
      },
      "developmentAreas": [
        {
          "area": "taste area to develop",
          "currentLevel": "current understanding",
          "nextSteps": "how to develop this area",
          "timeframe": "expected development timeframe"
        }
      ]
    }`;

  return callGeminiAI(prompt, AI_MODELS.PERSONALIZATION);
};

/**
 * Generate food and beverage pairing recommendations with scientific explanations
 */
export const generateAdvancedFoodPairings = async (
  beverageInfo,
  cuisinePreferences = []
) => {
  const prompt = `Create advanced food pairing recommendations for this beverage:
    
    Beverage: ${beverageInfo.name}
    Category: ${beverageInfo.category}
    Flavor Profile: ${JSON.stringify(
      beverageInfo.tastingNotes || beverageInfo.flavor
    )}
    ${
      cuisinePreferences.length
        ? `Preferred Cuisines: ${cuisinePreferences.join(", ")}`
        : ""
    }
    
    Provide scientific pairing analysis as JSON:
    {
      "pairingPrinciples": {
        "complement": "how beverage complements food flavors",
        "contrast": "how beverage contrasts food flavors", 
        "bridge": "how beverage bridges different elements"
      },
      "molecular": {
        "aromaCompounds": "key aroma compounds that interact",
        "tasteInteractions": "taste interaction mechanisms",
        "mouthfeelSynergy": "textural interaction effects"
      },
      "classicPairings": [
        {
          "dish": "traditional pairing dish",
          "region": "cultural origin",
          "science": "why this pairing works scientifically",
          "preparation": "optimal preparation method",
          "serving": "serving suggestions"
        }
      ],
      "innovativePairings": [
        {
          "dish": "modern/creative pairing",
          "concept": "pairing concept",
          "technique": "culinary technique involved",
          "innovation": "what makes this pairing unique"
        }
      ],
      "cuisineSpecific": {
        "asian": "Asian cuisine pairings",
        "mediterranean": "Mediterranean pairings",
        "american": "American cuisine pairings",
        "latinAmerican": "Latin American pairings",
        "middleEastern": "Middle Eastern pairings"
      },
      "seasonalPairings": {
        "spring": "spring seasonal pairings",
        "summer": "summer seasonal pairings",
        "fall": "fall seasonal pairings", 
        "winter": "winter seasonal pairings"
      },
      "avoidancePairings": [
        {
          "food": "food to avoid",
          "reason": "scientific reason for poor pairing",
          "alternative": "better alternative"
        }
      ],
      "temperature": {
        "hotFoods": "pairing with hot dishes",
        "coldFoods": "pairing with cold dishes",
        "temperatureSync": "temperature coordination tips"
      }
    }`;

  return callAnthropicAI(prompt, AI_MODELS.FOOD_PAIRING);
};

// ===== INVENTORY OPTIMIZATION & MARKET INTELLIGENCE =====

/**
 * Analyze inventory performance and provide optimization recommendations
 */
export const analyzeInventoryPerformance = async (
  inventoryData,
  salesData,
  seasonalTrends = null
) => {
  const prompt = `Analyze this beverage inventory and sales data to provide optimization recommendations:
    
    Current Inventory:
    ${inventoryData
      .map(
        (item) =>
          `- ${item.name} (${item.category}): ${item.quantity} units, $${
            item.price
          }, cost: $${item.cost || "unknown"}`
      )
      .join("\n")}
    
    Sales Data (last 30 days):
    ${salesData
      .map(
        (item) =>
          `- ${item.name}: ${item.unitsSold} sold, revenue: $${item.revenue}`
      )
      .join("\n")}
    
    ${
      seasonalTrends ? `Seasonal Trends: ${JSON.stringify(seasonalTrends)}` : ""
    }
    
    Provide comprehensive inventory analysis as JSON:
    {
      "performanceMetrics": {
        "topPerformers": [
          {
            "name": "beverage name",
            "category": "beverage category",
            "turnoverRate": "inventory turnover rate",
            "profitMargin": "profit margin percentage",
            "revenueContribution": "percentage of total revenue"
          }
        ],
        "underperformers": [
          {
            "name": "beverage name",
            "issue": "specific performance issue",
            "recommendation": "improvement strategy",
            "timeline": "expected improvement timeframe"
          }
        ],
        "fastMovers": "items with high velocity",
        "slowMovers": "items with low velocity"
      },
      "optimization": {
        "reorderRecommendations": [
          {
            "item": "item name",
            "currentStock": "current quantity",
            "recommendedOrder": "suggested order quantity",
            "urgency": "high/medium/low",
            "reasoning": "why this quantity is recommended"
          }
        ],
        "discontinuation": [
          {
            "item": "item to consider discontinuing",
            "reason": "performance issues",
            "alternative": "suggested replacement",
            "transitionPlan": "how to transition"
          }
        ],
        "newAdditions": [
          {
            "category": "suggested category",
            "specificItem": "suggested specific item", 
            "marketJustification": "market demand reasoning",
            "expectedPerformance": "performance projections"
          }
        ]
      },
      "pricingOptimization": {
        "priceIncreases": [
          {
            "item": "item name",
            "currentPrice": "current price",
            "recommendedPrice": "suggested price",
            "demandElasticity": "price sensitivity analysis",
            "revenueImpact": "projected revenue change"
          }
        ],
        "promotionalOpportunities": [
          {
            "item": "item for promotion",
            "strategy": "promotional strategy",
            "timing": "optimal timing",
            "expectedOutcome": "expected results"
          }
        ]
      },
      "seasonalStrategy": {
        "upcoming": "recommendations for next season",
        "cyclical": "year-round cyclical patterns",
        "specialEvents": "event-based opportunities"
      },
      "financialProjections": {
        "revenueImpact": "projected revenue changes",
        "costSavings": "projected cost reductions",
        "inventoryValue": "optimized inventory value",
        "cashFlowImprovement": "cash flow impact"
      }
    }`;

  return callGeminiAI(prompt, AI_MODELS.INVENTORY_OPTIMIZATION);
};

/**
 * Generate market trend analysis and competitive intelligence
 */
export const generateMarketIntelligence = async (
  category,
  region = "global",
  timeframe = "12 months"
) => {
  const prompt = `Provide comprehensive market intelligence for the ${category} category:
    
    Scope: ${region} market
    Timeframe: ${timeframe}
    
    Generate market analysis as JSON:
    {
      "marketOverview": {
        "marketSize": "current market size and value",
        "growthRate": "annual growth rate",
        "keyDrivers": ["main growth drivers"],
        "challenges": ["market challenges"],
        "opportunities": ["emerging opportunities"]
      },
      "trendAnalysis": {
        "emerging": [
          {
            "trend": "specific trend name",
            "description": "trend description",
            "impact": "high/medium/low market impact",
            "timeline": "adoption timeline",
            "opportunity": "business opportunity"
          }
        ],
        "declining": [
          {
            "trend": "declining trend",
            "reason": "why it's declining",
            "alternatives": ["what's replacing it"],
            "timeline": "decline timeline"
          }
        ],
        "stable": "established market segments"
      },
      "consumerBehavior": {
        "demographics": {
          "primaryAge": "primary age group",
          "secondaryAge": "secondary age group",
          "genderSplit": "gender distribution",
          "incomeLevel": "income bracket"
        },
        "preferences": {
          "flavor": "flavor trend preferences",
          "price": "price sensitivity",
          "packaging": "packaging preferences",
          "sustainability": "sustainability concerns"
        },
        "purchasingPatterns": {
          "channels": "preferred purchase channels",
          "frequency": "purchase frequency",
          "occasions": "consumption occasions",
          "discovery": "how consumers discover new products"
        }
      },
      "competitiveAnalysis": {
        "marketLeaders": [
          {
            "brand": "leading brand",
            "marketShare": "estimated market share",
            "strengths": ["competitive strengths"],
            "vulnerabilities": ["potential weaknesses"]
          }
        ],
        "emergingBrands": [
          {
            "brand": "emerging brand", 
            "differentiator": "unique selling proposition",
            "growth": "growth trajectory",
            "threat": "threat level to established players"
          }
        ],
        "pricePositioning": "market price positioning analysis"
      },
      "innovation": {
        "productInnovation": "new product development trends",
        "technologyAdoption": "technology integration trends",
        "sustainability": "environmental innovation trends",
        "packaging": "packaging innovation trends"
      },
      "regionalInsights": {
        "localPreferences": "region-specific preferences",
        "culturalFactors": "cultural influences",
        "regulations": "regulatory environment",
        "distributionChannels": "dominant distribution channels"
      },
      "futureOutlook": {
        "predictions": "market predictions for next 2-3 years",
        "disruptors": "potential market disruptors",
        "investments": "recommended investment areas",
        "risks": "potential risks to monitor"
      }
    }`;

  return callAnthropicAI(prompt, AI_MODELS.MARKET_ANALYSIS);
};

/**
 * Generate event and seasonal menu optimization recommendations
 */
export const generateEventMenuOptimization = async (
  eventType,
  seasonInfo,
  targetAudience,
  currentMenu
) => {
  const prompt = `Optimize beverage menu for this specific event and context:
    
    Event Type: ${eventType}
    Season: ${seasonInfo.season} ${seasonInfo.year}
    Target Audience: ${JSON.stringify(targetAudience)}
    
    Current Menu Highlights:
    ${currentMenu
      .slice(0, 10)
      .map((item) => `- ${item.name} (${item.category}): $${item.price}`)
      .join("\n")}
    
    Provide event optimization strategy as JSON:
    {
      "menuStrategy": {
        "featured": [
          {
            "beverage": "specific beverage to feature",
            "reason": "why perfect for this event",
            "presentation": "how to present/market it",
            "pairing": "suggested food pairing",
            "pricing": "pricing strategy"
          }
        ],
        "seasonal": [
          {
            "beverage": "seasonal recommendation",
            "seasonality": "why it fits the season",
            "availability": "seasonal availability notes",
            "marketing": "seasonal marketing angle"
          }
        ],
        "crowd": [
          {
            "beverage": "crowd-pleasing option",
            "appeal": "broad appeal factors",
            "scalability": "ability to serve large groups",
            "cost": "cost effectiveness"
          }
        ]
      },
      "eventSpecific": {
        "signature": "signature drink for this event",
        "nonAlcoholic": "featured non-alcoholic options",
        "budgetFriendly": "budget-conscious options",
        "premium": "premium upsell opportunities"
      },
      "operationalConsiderations": {
        "preparation": "prep requirements",
        "service": "service considerations",
        "inventory": "inventory planning",
        "staffing": "staffing requirements"
      },
      "marketingStrategy": {
        "theme": "marketing theme",
        "promotions": "promotional opportunities",
        "social": "social media strategies",
        "upselling": "upselling techniques"
      },
      "expectedOutcomes": {
        "revenueProjection": "revenue impact projection",
        "customerSatisfaction": "satisfaction improvement",
        "operationalEfficiency": "efficiency gains",
        "brandBuilding": "brand enhancement opportunities"
      }
    }`;

  return callGeminiAI(prompt, AI_MODELS.INVENTORY_OPTIMIZATION);
};

// Science-Based Wine-Meal Pairing Integration
// The pairing service is available as a separate import:
// import { pairingService } from './pairingService.js';

/**
 * PHASE 2 COMPATIBILITY EXPORTS
 *
 * These exports maintain backward compatibility while directing users
 * to the newer Phase 2 services for enhanced functionality.
 */

// Legacy class wrapper for backward compatibility
export class LegacyAIService {
  constructor() {
    console.warn(
      "LegacyAIService is deprecated. Please use masterServiceOrchestrator.js for new implementations."
    );
    this.aiServiceIntegration = new AIServiceIntegration();
  }

  async generateTastingNotes(beverage) {
    return generateTastingNotes(beverage);
  }

  async generateRatings(beverage) {
    return generateRatings(beverage);
  }

  async generateComprehensiveWineInfo(beverage) {
    return generateComprehensiveWineInfo(beverage);
  }

  async generateSpiritsProfile(beverage) {
    return this.aiServiceIntegration.generateSpiritsAnalysis(beverage);
  }

  async generateBeerAnalysis(beverage) {
    return this.aiServiceIntegration.generateBeerAnalysis(beverage);
  }

  async generateCocktailOptimization(beverage) {
    return this.aiServiceIntegration.generateCocktailOptimization(beverage);
  }
}

/**
 * Generic content generation method for backward compatibility
 * Automatically selects the best AI model for the task
 */
const generateContent = async (prompt, options = {}) => {
  const model = options.model || AI_MODELS.FOOD_PAIRING;

  try {
    if (model.includes("claude")) {
      const response = await callAnthropicAI(prompt, {
        model,
        maxTokens: options.maxTokens || 4000,
        temperature: options.temperature || 0.7,
      });
      return response;
    } else {
      const response = await callGeminiAI(prompt, {
        model: model === "gemini-pro" ? model : "gemini-pro",
        maxTokens: options.maxTokens || 4000,
        temperature: options.temperature || 0.7,
      });
      return response;
    }
  } catch (error) {
    console.error("Error in generateContent:", error);
    throw error;
  }
};

// Default export for backward compatibility
const aiService = {
  generateTastingNotes,
  generateRatings,
  generateImageSuggestions,
  generateComprehensiveWineInfo,
  generatePersonalizedRecommendations,
  callAnthropicAI,
  callGeminiAI,
  generateContent, // Add the new method
  AI_MODELS,
  LegacyAIService,
};

export default aiService;
